// editCascade.js — cascade helpers for the EditTest form.
//
// These are JS ports of the Kotlin reference implementations:
//   - AmrieOrganismHierarchy.kt → buildOrganismMatchTuples
//   - SpeciesMaster.kt → amrieHostsToTry
//   - MetadataScreen.kt cascade block → filterClinicalIndications
//
// Keep these in sync with the Kotlin originals when the cascade rules
// evolve. The Python `amrie_lookup.py` reference is the source of truth
// for the BREAKPOINT logic; these helpers are about the CASCADE (what
// to offer the user as valid options), which is a different concern
// that lives only on the clients.

// --------------------------------------------------------------------
// HIERARCHY_LEVELS — most-specific to least-specific.
// Used by buildOrganismMatchTuples to enumerate the (level, code) pairs
// a breakpoint rule could match an organism at.
// --------------------------------------------------------------------
export const HIERARCHY_LEVELS = [
  "SEROVAR_GROUP",
  "WHONET_ORG_CODE",
  "SPECIES_GROUP",
  "GENUS_CODE",
  "GENUS_GROUP",
  "FAMILY_CODE",
  "ANAEROBE+SUBKINGDOM_CODE",
  "ANAEROBE",
  "ALL",
];

/**
 * For an organism, build the ordered list of [level, code] pairs that
 * a breakpoint rule could match it at, from most specific to least.
 *
 * Mirrors AmrieOrganismHierarchy.buildOrganismMatchTuples in Kotlin.
 *
 * Example for E. coli (whonetOrgCode="eco", genusCode="ESC", familyCode="EBC"):
 *   [
 *     ["WHONET_ORG_CODE", "eco"],
 *     ["GENUS_CODE", "ESC"],
 *     ["FAMILY_CODE", "EBC"],
 *     ["ALL", ""]
 *   ]
 */
export function buildOrganismMatchTuples(org) {
  if (!org) return [];
  const tuples = [];

  // Level 1: SEROVAR_GROUP
  if (org.serovarGroup && org.serovarGroup.trim()) {
    tuples.push(["SEROVAR_GROUP", org.serovarGroup]);
  }

  // Level 2: WHONET_ORG_CODE — always present
  if (org.whonetOrgCode) {
    tuples.push(["WHONET_ORG_CODE", org.whonetOrgCode]);
  }

  // Level 3: SPECIES_GROUP
  if (org.speciesGroup && org.speciesGroup.trim()) {
    tuples.push(["SPECIES_GROUP", org.speciesGroup]);
  }

  // Level 4: GENUS_CODE
  if (org.genusCode && org.genusCode.trim()) {
    tuples.push(["GENUS_CODE", org.genusCode]);
  }

  // Level 5: GENUS_GROUP
  if (org.genusGroup && org.genusGroup.trim()) {
    tuples.push(["GENUS_GROUP", org.genusGroup]);
  }

  // Level 6: FAMILY_CODE
  if (org.familyCode && org.familyCode.trim()) {
    tuples.push(["FAMILY_CODE", org.familyCode]);
  }

  // Levels 7+8: anaerobe levels (only if the organism is an anaerobe)
  if (org.anaerobe === "X") {
    if (org.subkingdomCode === "+") {
      tuples.push(["ANAEROBE+SUBKINGDOM_CODE", "AN+"]);
    } else if (org.subkingdomCode === "-") {
      tuples.push(["ANAEROBE+SUBKINGDOM_CODE", "AN-"]);
    }
    tuples.push(["ANAEROBE", "ANA"]);
  }

  // Level 9: ALL — universal fallback. Code is empty by convention; the
  // breakpoint matcher checks ORGANISM_CODE_TYPE === 'ALL' and ignores
  // the code.
  tuples.push(["ALL", ""]);

  return tuples;
}

/**
 * Returns the AMRIE host strings to try for a species, in priority order.
 * Mirrors SpeciesMaster.amrieHostsToTry in Kotlin.
 *
 * For most species this is a singleton list. For a few (currently just
 * Horses) it's multiple host names that should all be tried.
 *
 * Returns an empty list if the species has no AMRIE host (e.g. manual
 * additions like "Other"). The caller can then fall back to ["Human"]
 * if the org's primaryGuideline allows fallback.
 */
export function amrieHostsToTry(species) {
  if (!species) return [];
  if (
    Array.isArray(species.amrieHostList) &&
    species.amrieHostList.length > 0
  ) {
    return species.amrieHostList;
  }
  if (species.amrieHost) {
    return [species.amrieHost];
  }
  return [];
}

/**
 * Compute the ordered list of clinical indication options for a given
 * (organism, species) pair. Mirrors the cascade in MetadataScreen.kt
 * lines 209-264.
 *
 * Algorithm:
 *   1. Determine the candidate hosts for the species:
 *        - human track → ["Human"]
 *        - vet track → species.amrieHostsToTry() (or ["Human"] if empty,
 *          to support fallback-only species)
 *   2. If both organism AND species are selected: walk the breakpoint
 *      table, collecting siteOfInfection values for breakpoints that
 *      match the organism at any hierarchy level AND have a host in
 *      the candidate list.
 *   3. If only species is selected (organism still being typed):
 *      collect siteOfInfection values for any breakpoint with a host
 *      in the candidate list.
 *   4. ALSO walk the org's custom breakpoints (when both organism and
 *      species are selected) and union any matching siteOfInfection
 *      values into the same set. This ensures sites that only exist
 *      because of a custom breakpoint rule are reachable from the
 *      dropdown — without this step, custom rules for site/organism
 *      combinations AMRIE doesn't cover would be unreachable from the
 *      cascade even though the matcher would happily use them. (B1
 *      cascade-reachability fix; see interpretation_algorithm.md
 *      Section 7.2.) Inactive custom breakpoints (active === false)
 *      are skipped.
 *   5. If neither organism nor species is selected: empty list.
 *
 * Returns an array of { code, display } objects. The first entry is
 * always { code: null, display: "General / Not specified" } — the
 * "no specific site" option that's available for every cascade.
 *
 * `allCustomBreakpoints` is optional; omitting it produces identical
 * behavior to the pre-B1 implementation.
 */
export function filterClinicalIndications({
  organism,
  species,
  allBreakpoints,
  allCustomBreakpoints,
  generalLabel = "General / Not specified",
}) {
  const general = { code: null, display: generalLabel };

  if (!species) {
    return [general];
  }

  let hostsToTry;
  if (species.track === "human") {
    hostsToTry = ["Human"];
  } else {
    hostsToTry = amrieHostsToTry(species);
    if (hostsToTry.length === 0) {
      // Fallback-only species (manual additions). Use Human breakpoints
      // to give the editor SOMETHING to pick from.
      hostsToTry = ["Human"];
    }
  }

  if (!Array.isArray(allBreakpoints) || allBreakpoints.length === 0) {
    // No AMRIE data at all — but custom breakpoints (if any) might still
    // contribute sites. Fall through to the custom-breakpoint loop below
    // rather than returning early.
  }

  const matchTuples = organism ? buildOrganismMatchTuples(organism) : null;
  const hostSet = new Set(hostsToTry);
  const sites = new Set();

  // --- AMRIE breakpoints ---
  if (Array.isArray(allBreakpoints) && allBreakpoints.length > 0) {
    if (matchTuples && matchTuples.length > 0) {
      // Both organism + species selected — full hierarchy filter.
      for (const bp of allBreakpoints) {
        if (!hostSet.has(bp.host)) continue;
        const site = bp.siteOfInfection;
        if (!site || !site.trim()) continue;
        const matchesOrganism = matchTuples.some(([level, code]) => {
          if (level === "ALL") return bp.organismCodeType === "ALL";
          return bp.organismCodeType === level && bp.organismCode === code;
        });
        if (matchesOrganism) sites.add(site);
      }
    } else {
      // Species only — fall back to host-level filter.
      for (const bp of allBreakpoints) {
        if (!hostSet.has(bp.host)) continue;
        const site = bp.siteOfInfection;
        if (!site || !site.trim()) continue;
        sites.add(site);
      }
    }
  }

  // --- Custom breakpoints (B1 fix) ---
  // Only union when both organism and species are selected; the host-only
  // fallback path doesn't apply because custom rules are always tied to a
  // specific organism (custom rules for ORGANISM_CODE_TYPE='ALL' would
  // still match here, just like AMRIE's ALL-type rules).
  if (
    Array.isArray(allCustomBreakpoints) &&
    allCustomBreakpoints.length > 0 &&
    matchTuples &&
    matchTuples.length > 0
  ) {
    for (const cbp of allCustomBreakpoints) {
      if (cbp.active === false) continue;
      if (!hostSet.has(cbp.host)) continue;
      const site = cbp.siteOfInfection;
      if (!site || !site.trim()) continue;
      const matchesOrganism = matchTuples.some(([level, code]) => {
        if (level === "ALL") return cbp.organismCodeType === "ALL";
        return cbp.organismCodeType === level && cbp.organismCode === code;
      });
      if (matchesOrganism) sites.add(site);
    }
  }

  const ordered = [...sites].sort();
  return [general, ...ordered.map((s) => ({ code: s, display: s }))];
}

/**
 * Returns the available potencies for a given antibiotic. Reads the
 * pre-aggregated `potencies` field on the antibiotic doc, which is
 * built from AMRIE's Antibiotics.txt and lists every known disk
 * strength for that drug — including ones for which no breakpoint
 * rule has been published.
 *
 * Same source as the Android AssessDiskScreen, which reads
 * antibiotic.potencies directly. Critical that web and Android use the
 * same list, or stored disk potencies from one client may not appear
 * in the other client's dropdown.
 *
 * Returns the antibiotic's potency list as-is (no sort) so the order
 * matches Android's display order.
 */
export function getPotenciesForAntibiotic(antibioticCode, allAntibiotics) {
  if (!antibioticCode || !Array.isArray(allAntibiotics)) return [];
  const drug = allAntibiotics.find((a) => a.whonetAbxCode === antibioticCode);
  if (!drug || !Array.isArray(drug.potencies)) return [];
  return drug.potencies;
}
